import type { GameId } from "../modules/common/model/models";

const apiUrl =
  process.env.NEXT_PUBLIC_API_URL || "http://localhost:10001/api/v2/";

const setRoute = (subRoute: any) => `${apiUrl}${subRoute}`;

// Bump when the chart endpoint's response shape changes. The chart fetchers
// append this as a `?v=` query param so Next.js/browser caches don't serve a
// previous-shape response across deploys — the URL itself is the cache key,
// and a new value forces a fresh fetch. Backend ignores the param.
export const CHART_RESPONSE_VERSION = "3";

// Bump when the public game-manifest response contract changes. The query is
// ignored by the API, but bypasses legacy Cloudflare Cache API entries whose
// cache keys predate the current public-URL/tag invalidation scheme.
export const GAME_MANIFEST_RESPONSE_VERSION = "2";

// Bump when card lists, set metadata, or set-scoped facet semantics change.
// Catalogue responses are cached by URL at the edge and in Next.js, so a new
// value prevents a previous contract from surviving an API deployment.
export const CARD_CATALOG_RESPONSE_VERSION = "3";

// Page size for the infinite browse lists (city, LGS, collection, trade
// lists). One page has to be comfortably taller than a screen: the next page
// is fetched when the end of the list comes within NEXT_PAGE_MARGIN_SCREENS
// viewports of the bottom, so if a page is barely a screen tall then a
// lookahead wide enough to stay ahead of a scroll also fires on the very
// first render.
//
// MUST be identical on the server prefetch and the client filter hook for a
// given surface — the request object is part of the React Query key, so a
// mismatch means the hydrated page is never read and every list refetches on
// mount.
export const BROWSE_PAGE_SIZE = 30;

// Bump when GET /sets/compact changes shape. The v2 contract is keyed by
// game and then set code; v1 collapsed every game into a code-only map.
export const SETS_COMPACT_RESPONSE_VERSION = "2";

export const API_ROUTES = {
  GAMES: setRoute(`games?v=${GAME_MANIFEST_RESPONSE_VERSION}`),
  AUTH: {
    LOGIN: setRoute("auth/login"),
    REGISTER: setRoute("auth/register"),
    FORGOT_PASSWORD: setRoute("auth/forgot-password"),
  },
  SETS: {
    GET_ALL: setRoute(`sets/compact?v=${SETS_COMPACT_RESPONSE_VERSION}`),
    GET_BY_CODE_NUMBER: (game: GameId, code: string, cardNumber: string) =>
      setRoute(`sets/${game}/${code}/cards/${cardNumber}`),
    PAGED: (game: GameId) => setRoute(`sets/paged/${game}`),
    SET: (code: string) => setRoute(`sets/${code}`),
    SEALED_PRODUCTS: (code: string) => setRoute(`sets/${code}/sealed-product`),
    SEALED_PRODUCT_BY_SLUG: (code: string, slug: string) =>
      setRoute(`sets/${code}/sealed-product/${slug}`),
  },
  COLLECTIONS: {
    GET_USER: setRoute("collections"),
    ADD_CARD: (id: string) => setRoute(`collections/${id}/cards`),
    ADD_CARD_BULK: (id: string) => setRoute(`collections/${id}/cards/bulk`),
    DELETE_BULK_CARDS: (id: string) => setRoute(`collections/${id}/cards/bulk`),
    CREATE: setRoute("collections/create"),
    REMOVE_CARD: (id: string) => (cardId: string) =>
      setRoute(`collections/${id}/cards/${cardId}`),
    UPDATE_CARD_GROUP: (id: string) => setRoute(`collections/${id}/cards`),
    CARDS: (id: string) => setRoute(`collections/${id}/cards`),
    SETS: (id: string) => setRoute(`collections/${id}/sets`),
    FACETS: (id: string) => setRoute(`collections/${id}/cards/facets`),
    SELECT_ALL: (id: string) => setRoute(`collections/${id}/cards/select-all`),
    IMPORT: (id: string) => setRoute(`collections/${id}/imports`),
    IMPORTS_PAGED: (id: string) => setRoute(`collections/${id}/imports`),
    IMPORT_BY_ID: (id: string) => (impId: string) =>
      setRoute(`collections/${id}/imports/${impId}`),
    IMPORT_ROLLBACK: (id: string) => (impId: string) =>
      setRoute(`collections/${id}/imports/${impId}/rollback`),
    ADD_CARDS_TO_LIST: (collectionId: string) =>
      setRoute(`collections/${collectionId}/cards/add-to-list`),
    REMOVE_CARDS_FROM_LIST: (collectionId: string) =>
      setRoute(`collections/${collectionId}/cards/remove-from-list`),
  },
  DECKS: {
    GET_USER: setRoute("decks"),
    CREATE: setRoute("decks"),
    IMPORT_ARENA: (deckName: string) =>
      setRoute(`decks/import-arena/${deckName}`),
    GET_BY_ID: (id: string) => setRoute(`decks/${id}`),
    DELETE: (id: string) => setRoute(`decks/${id}`),
    ADD_CARD: (id: string) => setRoute(`decks/${id}/add-card`),
    REMOVE_CARD: (id: string) => setRoute(`decks/${id}/remove-card`),
    UPDATE_CARD: (id: string) => setRoute(`decks/${id}/update-card`),
    SPLIT_CARD: (id: string) => setRoute(`decks/${id}/split-card`),
    UPDATE: (id: string) => setRoute(`decks/${id}`),
    LATEST_PUBLIC: setRoute("decks/public/latest"),
    COPY: (id: string) => setRoute(`decks/${id}/copy`),
    ENGAGEMENT: (id: string) => setRoute(`decks/${id}/engagement`),
    FEATURED_CARD: (id: string) => setRoute(`decks/${id}/featured-card`),
    LIKE: (id: string) => setRoute(`decks/${id}/like`),
    COMMENTS: (id: string) => setRoute(`decks/${id}/comments`),
    COMMENT: (id: string, commentId: string) =>
      setRoute(`decks/${id}/comments/${commentId}`),
  },
  MAGIC_CARDS: {
    PAGED: setRoute("cards"),
    FACETS: setRoute("cards/facets"),
    GET_BY_ID: (id: string) => setRoute(`cards/${id}`),
    AUTOCOMPLETE: setRoute("cards/autocomplete"),
    PRICES_SEARCH: setRoute("cards/prices-search"),
    PRINTINGS: (id: string) => setRoute(`cards/${id}/printings`),
    PRINTING_OPTIONS: (id: string) => setRoute(`cards/${id}/printing-options`),
    // Goes through a same-origin Next.js route handler at
    // /api/cards/[cardId]/chart that enforces Clerk auth on the server and
    // wraps the upstream Go API fetch in the Next.js Data Cache so all
    // signed-in users share a tag-invalidated cached response.
    CHART: (id: string) => `/api/cards/${id}/chart`,
    RESOLVE: setRoute("cards/resolve"),
    OWNERSHIP_STATUS: setRoute("cards/ownership-status"),
  },
  TRADE_LISTS: {
    GET_USER: setRoute("trades/lists"),
    CREATE: setRoute("trades/lists"),
    GET_BY_ID: (id: string) => setRoute(`trades/lists/${id}`),
    DELETE: (id: string) => setRoute(`trades/lists/${id}`),
    ADD_ITEM: (id: string) => setRoute(`trades/lists/${id}/items`),
    DELETE_ITEM: (id: string) => (itemId: string) =>
      setRoute(`trades/lists/${id}/items/${itemId}`),
    DELETE_BULK_ITEMS: (id: string) => setRoute(`trades/lists/${id}/items`),
    UPDATE_ITEM_GROUP: (id: string) => setRoute(`trades/lists/${id}/items`),
    MOVE_ITEMS: (id: string) => setRoute(`trades/lists/${id}/items/move`),
    MOVE_ITEMS_ACROSS_LISTS: setRoute("trades/lists/batch/items/move"),
    DELETE_ITEMS_ACROSS_LISTS: setRoute("trades/lists/batch/items"),
    TRADES: setRoute("trades"),
    PUBLISH: (id: string) => setRoute(`trades/lists/${id}/publish`),
    BULK_IMPORT: (id: string) => setRoute(`trades/lists/${id}/items/import`),
    IMPORT: (id: string) => setRoute(`trades/lists/${id}/imports`),
    IMPORT_BY_ID: (id: string, impId: string) =>
      setRoute(`trades/lists/${id}/imports/${impId}`),
    IMPORT_ROLLBACK: (id: string, impId: string) =>
      setRoute(`trades/lists/${id}/imports/${impId}/rollback`),
    SELECT_ALL: (id: string) => setRoute(`trades/lists/${id}/items/select-all`),
  },
  IMPORT: {
    PARSE_TEXT: setRoute("import/parse-text"),
    CREATE_DRAFT: setRoute("import/drafts"),
    DRAFT_ROWS: (draftId: string) => setRoute(`import/drafts/${draftId}/rows`),
    UPDATE_DRAFT_ROW: (draftId: string, index: number) =>
      setRoute(`import/drafts/${draftId}/rows/${index}`),
    TOGGLE_ANY_PRINTING: (draftId: string, index: number) =>
      setRoute(`import/drafts/${draftId}/rows/${index}/any-printing`),
    CONFIRM_DRAFT: (draftId: string) =>
      setRoute(`import/drafts/${draftId}/confirm`),
  },
  USERS: {
    SET_LGS: (id: string) => setRoute(`users/${id}/lgs`),
    GET_LGS: (id: string) => setRoute(`users/${id}/lgs`),
    PREVIEW: (id: string) => setRoute(`users/${id}/preview`),
    PREVIEW_BY_USERNAME: (username: string) =>
      setRoute(`users/preview?username=${encodeURIComponent(username)}`),
    MATCHED_TRADES: (id: string) => setRoute(`users/${id}/matched-trades`),
    GET: setRoute(`users`),
    PREFERENCES: (userId: string) => setRoute(`users/${userId}/preferences`),
    NOTIFICATION_PREFERENCES: (userId: string) =>
      setRoute(`users/${userId}/notification-preferences`),
    SHOW_LGS: (id: string) => setRoute(`users/${id}/show-lgs`),
    REFERRAL_CODE: (id: string) => setRoute(`users/${id}/referral-code`),
  },
  REFERRALS: {
    SUBMIT: setRoute("referrals"),
    STATS: setRoute("referrals/stats"),
    INVITES: setRoute("referrals/invites"),
    LEADERBOARD: setRoute("referrals/leaderboard"),
    CODE: (code: string) =>
      setRoute(`referrals/codes/${encodeURIComponent(code)}`),
  },
  ACQUISITIONS: {
    CLAIM: setRoute("acquisitions"),
    ME: setRoute("acquisitions/me"),
    CODE: (code: string) =>
      setRoute(`acquisitions/codes/${encodeURIComponent(code)}`),
  },
  INVITES: {
    CODE: (code: string) =>
      setRoute(`invites/codes/${encodeURIComponent(code)}`),
  },
  LGS: {
    GET: setRoute("lgs"),
    GET_LGS: (id: number) => setRoute(`lgs/${id}`),
    GET_LGS_BY_SLUG: (slug: string) => setRoute(`lgs/by-slug/${slug}`),
    GET_USERS: (id: number) => setRoute(`lgs/${id}/users`),
  },
  VERSION: {
    GET: setRoute("version"),
  },
  METAGAME: {
    AVAILABLE_FORMATS: setRoute("metagame/available-formats"),
    GET_BY_FORMAT: (format: string) =>
      setRoute(`metagame/${format}/archetypes`),
    GET_DECKS_BY_ARCHETYPE: (format: string) => (archetype: string) =>
      setRoute(`metagame/${format}/archetypes/${archetype}`),
    GET_DECK: (format: string) => (archetypeSlug: string) => (id: string) =>
      setRoute(`metagame/${format}/archetypes/${archetypeSlug}/decks/${id}`),
  },
  CHATS: {
    BASE: setRoute("chats"),
    MESSAGES: (id: string) => setRoute(`chats/${id}/messages`),
    BY_ID: (id: string) => setRoute(`chats/${id}`),
  },
  NOTIFICATIONS: {
    BASE: setRoute("notifications"),
    MARK_READ: (id: string) => setRoute(`notifications/${id}/read`),
    MARK_SHOWN: (id: string) => setRoute(`notifications/${id}/shown`),
  },
  PROPOSALS: {
    BASE: setRoute("trades/proposals"),
    BY_ID: (id: string) => setRoute(`trades/proposals/${id}`),
    ACCEPT: (id: string) => setRoute(`trades/proposals/${id}/accept`),
    DECLINE: (id: string) => setRoute(`trades/proposals/${id}/decline`),
    WITHDRAW: (id: string) => setRoute(`trades/proposals/${id}/withdraw`),
    COMPLETE: (id: string) => setRoute(`trades/proposals/${id}/complete`),
    ADD_CARDS: setRoute("trades/proposals/add-cards"),
    BY_CHAT: (chatId: string) => setRoute(`trades/proposals/chat/${chatId}`),
    LOCATION_OPTIONS: (chatId: string) =>
      setRoute(`trades/proposals/location-options/chat/${chatId}`),
  },
  TRADE: {
    GET_CARD_AVAILABILITY: setRoute(`trades/match-in-user-lgs`),
    CARD_CITIES: (
      setCode: string,
      number: string,
      game: string,
      cardId?: string
    ) => {
      const params = new URLSearchParams({ setCode, number });
      params.append("games", game);
      if (cardId) params.set("cardId", cardId);
      return setRoute(`trades/card-cities?${params.toString()}`);
    },
    MATCHED_TRADES_IN_LGS: setRoute(`trades/match-in-lgs`),
    MATCHED_TRADES_IN_USER: setRoute(`trades/match-in-user`),
    CITY_MATCHED_CARDS: setRoute(`trades/city-matched-cards`),
    CITY_STATS: setRoute(`trades/city-stats`),
    SATISFYING_ITEMS: setRoute(`trades/satisfying-items`),
    BROWSE: setRoute(`trades/browse`),
    BROWSE_AVAILABILITY: setRoute(`trades/browse/availability`),
    BROWSE_FACETS: setRoute(`trades/browse/facets`),
  },
  SITEMAP: {
    LGS: setRoute("sitemap/lgs"),
    CARDS: setRoute("sitemap/cards"),
    SEALED_PRODUCTS: setRoute("sitemap/sealed-products"),
  },
  BILLING: {
    ME: setRoute("billing/me"),
    RECONCILE: setRoute("billing/reconcile"),
  },
  WALLET: {
    CREDIT: (currency: string) =>
      setRoute(`wallet/credit?currency=${encodeURIComponent(currency)}`),
  },
  DROPS: {
    LIST: setRoute("drops"),
    GET: (dropId: string) => setRoute(`drops/${dropId}`),
    ME: (dropId: string) => setRoute(`drops/${dropId}/me`),
    PAYMENTS: (dropId: string) => setRoute(`drops/${dropId}/payments`),
    CONFIRM_PAYMENT: (dropId: string) =>
      setRoute(`drops/${dropId}/payments/confirm`),
    REVEAL: (dropId: string, orderId: string) =>
      setRoute(`drops/${dropId}/orders/${orderId}/reveal`),
  },
  BUYLIST: {
    CREATE_QUOTES: setRoute("buylist/quotes"),
    SUBMISSIONS: setRoute("buylist/submissions"),
    SUBMISSION_SHIPMENT: (submissionId: string) =>
      setRoute(`buylist/submissions/${submissionId}/shipment`),
  },
};
